import type { Database } from '../client'
import type { InsertPremium, SelectMembershipAddOn } from '../schema'
import type { DatabaseTransaction } from './child-merge'
import { MembershipAddOn, Premium } from '../schema'
import { releaseOrgTrialSeats, resolveAssignableChildren } from './mint-purchased-seats'

/** One pool the add-on joins, as `resolveAddOnChain` priced it. */
export interface AddOnPool {
  id: string
  startDate: Date
  endDate: Date
}

export interface MintAddOnSeatsArgs {
  invoiceId: string
  organizationId: string
  seats: number
  /** The chain in time order: the live pool first, then the terms queued behind it. */
  pools: AddOnPool[]
  /** When the invoice was paid. Seats never start before this, even on a pool already open. */
  paidAt: Date
  /**
   * Whether to replay the invoice's `invoice_seat_assignment` rows onto the live segment.
   *
   * Defaults to true — a top-up seats the students the buyer named. FALSE for a RENEWAL's empty
   * seats: that invoice's assignment rows name the students CONTINUING onto the new pool's own
   * seats, and stamping them onto the chain as well would either fail the overlap check or, for a
   * student whose current seat is this organization's free trial, release that trial seat and
   * hand them a chain seat instead of the continuation they were bought.
   */
  assignChildren?: boolean
}

export interface MintAddOnSeatsResult {
  addOns: SelectMembershipAddOn[]
  seatsCreated: number
  /** Children stamped onto a seat on the LIVE pool, in seat order. */
  assignedChildIds: string[]
  /** Children chosen at checkout who no longer qualify — left the school, or seated since. */
  skippedChildIds: string[]
  releasedTrialSeatIds: string[]
}

/**
 * Fulfilling an add-on: one `MembershipAddOn` per pool in the chain, its seats, and the buyer's
 * chosen children stamped onto them.
 *
 * Seats join EVERY pool the organization has already paid for, not only the live one — a school that
 * renewed before topping up expects the new seats to last as long as the rest. Each add-on rides its
 * own pool's window, which is why the chain is minted pool by pool rather than as one long seat.
 *
 * ONE TRANSACTION, so call it with `dbPool`: `db` is the neon-http handle whose `.transaction()`
 * throws at runtime, and locally both handles are node-postgres, so the wrong one passes every local
 * run and fails only after deploy. Same rule as `mintPurchasedSeats`.
 *
 * Assignment is re-validated PER POOL against that pool's own window, not once for the chain. The
 * windows are disjoint and half-open, so a child stamped on the live pool is not blocked from the
 * pool that opens the day it closes — `no_overlapping_child_seats` permits exactly that, and it is
 * the whole point of the chain. A child who becomes ineligible partway (seated elsewhere for a later
 * term) simply drops out of that pool's selection while keeping the seats already minted.
 *
 * Not idempotent: a second call for the same invoice mints a second set of add-ons. The caller's
 * "does this invoice already have add-ons" check is what makes retries safe.
 */
export async function mintAddOnSeats(database: Database, args: MintAddOnSeatsArgs): Promise<MintAddOnSeatsResult> {
  return database.transaction((tx) => mintAddOnSeatsInTransaction(tx, args))
}

/**
 * The body of {@link mintAddOnSeats}, on a transaction the caller already holds — so a RENEWAL can
 * mint its pool and the chain its empty seats ride together (`mintRenewalSeats`).
 */
export async function mintAddOnSeatsInTransaction(
  tx: DatabaseTransaction,
  { invoiceId, organizationId, seats, pools, paidAt, assignChildren = true }: MintAddOnSeatsArgs,
): Promise<MintAddOnSeatsResult> {
  if (!Number.isInteger(seats) || seats < 1) {
    throw new Error(`Cannot mint ${seats} add-on seats for invoice ${invoiceId}: seats must be a positive integer`)
  }

  if (pools.length === 0) {
    throw new Error(`Cannot mint add-on seats for invoice ${invoiceId}: no pool to join`)
  }

  const addOns: SelectMembershipAddOn[] = []
  const releasedTrialSeatIds: string[] = []

  let seatsCreated = 0
  let assignedChildIds: string[] = []
  let skippedChildIds: string[] = []

  for (const [index, pool] of pools.entries()) {
    /**
     * The live pool's seats start now; a queued pool's start when that pool does. Clamped so a
     * pool that opened while the invoice sat unpaid still starts from payment rather than
     * back-dating seats across a window that has partly elapsed.
     */
    const startDate = pool.startDate > paidAt ? pool.startDate : paidAt

    const [addOn] = await tx
      .insert(MembershipAddOn)
      .values({
        seats,
        invoiceId,
        organizationId,
        membershipId: pool.id,
        startDate,
        endDate: pool.endDate,
      })
      .returning()

    if (!addOn) throw new Error(`Failed to create membership add on for pool ${pool.id}`)

    const { assignable, skipped } = assignChildren
      ? await resolveAssignableChildren(tx, {
          invoiceId,
          organizationId,
          seats: addOn.seats,
          startDate: addOn.startDate,
          endDate: addOn.endDate,
        })
      : { assignable: [], skipped: [] }

    // Released before the stamping insert, exactly as in `mintPurchasedSeats`: the EXCLUDE
    // constraint is checked per statement, so a trial seat has to be out of scope by the time the
    // paid seat lands.
    const released = await releaseOrgTrialSeats(tx, { organizationId, childIds: assignable })
    releasedTrialSeatIds.push(...released)

    /**
     * `Array.from` with a fresh object per slot. `new Array(n).fill(obj)` puts ONE shared object
     * in every slot, which is invisible while the rows are identical and wrong the moment a seat
     * carries a child of its own.
     */
    const rows: InsertPremium[] = Array.from({ length: addOn.seats }, (_, seatIndex) => ({
      organizationId,
      source: 'add_on' as const,
      membershipAddOnId: addOn.id,
      startDate: addOn.startDate,
      endDate: addOn.endDate,
      childId: assignable[seatIndex] ?? null,
    }))

    const created = await tx.insert(Premium).values(rows).returning()

    seatsCreated += created.length
    addOns.push(addOn)

    // Reported from the LIVE pool — the one the buyer was looking at. A later pool's selection can
    // only be the same or smaller, and reporting the union would overstate what was seated today.
    if (index === 0) {
      assignedChildIds = assignable
      skippedChildIds = skipped
    }
  }

  return { addOns, seatsCreated, assignedChildIds, skippedChildIds, releasedTrialSeatIds }
}
